import React, { useMemo, useState } from 'react'
import type { EntryType, LogEntry } from '../types'
import { useTracker } from '../context/TrackerContext'
import PhotoModal, { type PhotoSize } from './PhotoModal'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ''
const AMOUNT_OPTIONS = [1, 2, 3, 4, 5, 10]

interface Filters {
  q: string
  from: string
  to: string
}

export default function History({ onRefresh }: { onRefresh: () => void }) {
  const { logs, deleteLog, clearLogs, addCategoryFromLog, settings, editLog } = useTracker()
  const [q, setQ] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [filters, setFilters] = useState<Filters>({ q: '', from: '', to: '' })
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [bulkDate, setBulkDate] = useState('')
  const [bulkTime, setBulkTime] = useState('')
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [bulkSaving, setBulkSaving] = useState(false)
  const [bulkError, setBulkError] = useState<string>()
  const [editingId, setEditingId] = useState<string>()
  const [editCategory, setEditCategory] = useState('')
  const [editQuantityInput, setEditQuantityInput] = useState('1')
  const [editNotes, setEditNotes] = useState('')
  const [editDate, setEditDate] = useState('')
  const [editError, setEditError] = useState<string>()
  const [editSaving, setEditSaving] = useState(false)
  const [editType, setEditType] = useState<EntryType>('did')
  const [photoModal, setPhotoModal] = useState<{
    smallUrl?: string
    largeUrl?: string
    initialSize: PhotoSize
  } | null>(null)

  const filteredLogs = useMemo(() => applyFilters(logs, filters), [logs, filters])
  const selectedLogs = useMemo(
    () => selectedIds.map(id => logs.find(log => log.id === id)).filter((log): log is LogEntry => Boolean(log)),
    [logs, selectedIds]
  )
  const parsedEditQuantity = Number.parseInt(editQuantityInput, 10)
  const normalizedEditQuantity = Number.isFinite(parsedEditQuantity) ? Math.max(1, parsedEditQuantity) : 1

  function runSearch() {
    setFilters({ q, from, to })
  }

  function reset() {
    setQ('')
    setFrom('')
    setTo('')
    setFilters({ q: '', from: '', to: '' })
  }

  function startSelection() {
    setSelectMode(true)
    setSelectedIds([])
    cancelEdit()
  }

  function cancelSelection() {
    setSelectMode(false)
    setSelectedIds([])
  }

  function toggleSelection(id: string) {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(entryId => entryId !== id) : [...prev, id]))
  }

  function openBulkUpdate() {
    if (selectedIds.length === 0) return
    const first = selectedLogs[0]
    if (first) {
      const date = new Date(first.date)
      setBulkDate(formatDateInput(date))
      setBulkTime(formatTimeInput(date))
    } else {
      const now = new Date()
      setBulkDate(formatDateInput(now))
      setBulkTime(formatTimeInput(now))
    }
    setBulkError(undefined)
    setBulkModalOpen(true)
  }

  function closeBulkUpdate() {
    setBulkModalOpen(false)
    setBulkSaving(false)
    setBulkError(undefined)
  }

  function setBulkToYesterday(hour: number) {
    const target = new Date()
    target.setDate(target.getDate() - 1)
    target.setHours(hour, 0, 0, 0)
    setBulkDate(formatDateInput(target))
    setBulkTime(formatTimeInput(target))
  }

  async function applyBulkUpdate() {
    if (bulkSaving) return
    const isoDate = parseDateTimeParts(bulkDate, bulkTime)
    if (!isoDate) {
      setBulkError('Date and time are required')
      return
    }
    setBulkSaving(true)
    setBulkError(undefined)
    try {
      await Promise.all(selectedIds.map(id => editLog(id, { performedAt: isoDate })))
      closeBulkUpdate()
      cancelSelection()
    } catch (err) {
      setBulkError((err as Error).message || 'Unable to update entries')
    } finally {
      setBulkSaving(false)
    }
  }

  async function remove(id: string) {
    await deleteLog(id)
    if (editingId === id) {
      cancelEdit()
    }
  }

  async function nuke() {
    if (window.confirm('Clear all history?')) {
      await clearLogs()
    }
  }

  async function favorite(log: LogEntry) {
    await addCategoryFromLog(log.type as EntryType, log.category)
  }

  function startEditing(log: LogEntry) {
    setEditSaving(false)
    setEditingId(log.id)
    setEditCategory(log.category)
    setEditQuantityInput(String(log.quantity ?? 1))
    setEditNotes(log.notes ?? '')
    setEditDate(formatDateTimeLocal(log.date))
    setEditError(undefined)
    setEditType(log.type as EntryType)
  }

  function cancelEdit() {
    setEditingId(undefined)
    setEditCategory('')
    setEditQuantityInput('1')
    setEditNotes('')
    setEditDate('')
    setEditError(undefined)
    setEditType('did')
  }

  async function saveEdit() {
    if (!editingId) return
    const trimmed = editCategory.trim()
    if (!trimmed) {
      setEditError('Category is required')
      return
    }
    const isoDate = parseDateTimeLocal(editDate)
    if (!isoDate) {
      setEditError('Date and time are required')
      return
    }
    setEditSaving(true)
    setEditError(undefined)
    try {
      await editLog(editingId, {
        category: trimmed,
        quantity: normalizedEditQuantity,
        type: editType,
        notes: editNotes.trim() || undefined,
        performedAt: isoDate,
      })
      cancelEdit()
    } catch (err) {
      setEditError((err as Error).message || 'Unable to update log')
    } finally {
      setEditSaving(false)
    }
  }

  function handleRowClick(log: LogEntry) {
    if (editSaving) return
    if (selectMode) {
      toggleSelection(log.id)
      return
    }
    if (editingId === log.id) return
    startEditing(log)
  }

  function exportJSON() {
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'my-tracker-logs.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  function openPhoto(log: LogEntry, size: PhotoSize = 'small') {
    const smallUrl = log.photoPreviewUrl ? `${API_BASE}${log.photoPreviewUrl}` : undefined
    const largeUrl = log.photoDownloadUrl ? `${API_BASE}${log.photoDownloadUrl}` : undefined
    if (!smallUrl && !largeUrl) {
      return
    }
    setPhotoModal({
      smallUrl,
      largeUrl,
      initialSize: size,
    })
  }

  function closePhoto() {
    setPhotoModal(null)
  }

  return (
    <div>
      <h3>History</h3>
      <div className="history-controls">
        <input placeholder="Search text" value={q} onChange={e => setQ(e.target.value)} />
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        <button onClick={runSearch} className="small button">Search</button>
        <button onClick={reset} className="small">Reset</button>
        <button
          onClick={selectMode ? openBulkUpdate : startSelection}
          className="small secondary"
          disabled={selectMode && selectedIds.length === 0}
        >
          {selectMode ? 'Update' : 'Select entries'}
        </button>
        {selectMode && (
          <button onClick={cancelSelection} className="small">Cancel</button>
        )}
        <button onClick={exportJSON} className="small">Export</button>
        <button onClick={nuke} className="small danger">Clear All</button>
        <button onClick={onRefresh} className="small secondary">Refresh</button>
      </div>

      <div className="list">
        {filteredLogs.length === 0 && <div style={{ marginTop: 8 }}>No logs</div>}
        {filteredLogs.map(l => {
          const isEditing = editingId === l.id
          return (
            <div
              key={l.id}
              className="row"
              onClick={() => handleRowClick(l)}
              style={{ cursor: isEditing || selectMode ? 'default' : 'pointer' }}
            >
              <div className="history-row-main">
                {selectMode && (
                  <label className="history-select" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(l.id)}
                      onChange={() => toggleSelection(l.id)}
                    />
                  </label>
                )}
                <div>
                {isEditing ? (
                  <div className="history-edit" onClick={e => e.stopPropagation()}>
                    <label style={{ display: 'block' }}>
                      Name
                      <input
                        value={editCategory}
                        onChange={e => setEditCategory(e.target.value)}
                        disabled={editSaving}
                        style={{ width: '100%', marginTop: 4 }}
                      />
                    </label>
                    <label style={{ display: 'block', marginTop: 8 }}>
                      Category group
                      <select
                        value={editType}
                        onChange={e => setEditType(e.target.value as EntryType)}
                        disabled={editSaving}
                        style={{ width: '100%', marginTop: 4 }}
                      >
                        <option value="did">Did</option>
                        <option value="ate">Ate</option>
                        <option value="drank">Drank</option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    <label style={{ display: 'block', marginTop: 8 }}>
                      Amount
                      <div className="amount-stepper" style={{ marginTop: 4 }}>
                        <button
                          type="button"
                          className="secondary small"
                          onClick={() => setEditQuantityInput(String(Math.max(1, normalizedEditQuantity - 1)))}
                          disabled={editSaving}
                        >
                          −
                        </button>
                        <input
                          inputMode="numeric"
                          value={editQuantityInput}
                          onChange={e => {
                            const nextValue = e.target.value
                            if (nextValue === '' || /^\d+$/.test(nextValue)) {
                              setEditQuantityInput(nextValue)
                            }
                          }}
                          onBlur={() => {
                            if (!editQuantityInput.trim()) {
                              setEditQuantityInput('1')
                            } else {
                              setEditQuantityInput(String(normalizedEditQuantity))
                            }
                          }}
                          disabled={editSaving}
                        />
                        <button
                          type="button"
                          className="secondary small"
                          onClick={() => setEditQuantityInput(String(normalizedEditQuantity + 1))}
                          disabled={editSaving}
                        >
                          +
                        </button>
                      </div>
                      <div className="amount-options">
                        {AMOUNT_OPTIONS.map(option => (
                          <button
                            key={option}
                            type="button"
                            className={`small ${normalizedEditQuantity === option ? 'button' : 'secondary'}`}
                            onClick={() => setEditQuantityInput(String(option))}
                            disabled={editSaving}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </label>
                    <label style={{ display: 'block', marginTop: 8 }}>
                      Notes
                      <textarea
                        value={editNotes}
                        onChange={e => setEditNotes(e.target.value)}
                        disabled={editSaving}
                        style={{ width: '100%', marginTop: 4, minHeight: 80, resize: 'vertical' }}
                      />
                    </label>
                    <label style={{ display: 'block', marginTop: 8 }}>
                      Logged at
                      <input
                        type="datetime-local"
                        value={editDate}
                        onChange={e => setEditDate(e.target.value)}
                        disabled={editSaving}
                        style={{ width: '100%', marginTop: 4 }}
                      />
                    </label>
                    <small style={{ color: '#666', display: 'block', marginTop: 6 }}>({l.type})</small>
                  </div>
                ) : (
                  <div style={{ fontWeight: 600 }}>
                    {l.category}
                    {l.quantity && l.quantity !== 1 && <span style={{ marginLeft: 6, color: '#333' }}>×{l.quantity}</span>}
                    <small style={{ color: '#666', marginLeft: 6 }}>({l.type})</small>
                  </div>
                )}
                <div style={{ color: '#666' }}>{new Date(l.date).toLocaleString()}</div>
                {l.notes && <div style={{ marginTop: 6 }}>{l.notes}</div>}
                {(l.photoPreviewUrl || l.photoDownloadUrl) && (
                  <div className="history-photo-actions" style={{ marginTop: 8 }}>
                    {l.photoPreviewUrl && (
                      <a
                        href={`${API_BASE}${l.photoPreviewUrl}`}
                        onClick={e => {
                          e.stopPropagation()
                          e.preventDefault()
                          openPhoto(l, 'small')
                        }}
                      >
                        View small
                      </a>
                    )}
                    {l.photoDownloadUrl && (
                      <a
                        href={`${API_BASE}${l.photoDownloadUrl}`}
                        onClick={e => {
                          e.stopPropagation()
                          e.preventDefault()
                          openPhoto(l, 'large')
                        }}
                      >
                        View large
                      </a>
                    )}
                  </div>
                )}
                {isEditing && editError && <div className="status error" style={{ marginTop: 8 }}>{editError}</div>}
                </div>
              </div>
              <div className="history-actions">
                {isEditing ? (
                  <>
                    <button
                      className="small"
                      onClick={e => {
                        e.stopPropagation()
                        saveEdit()
                      }}
                      disabled={editSaving}
                    >
                      {editSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      className="small secondary"
                      onClick={e => {
                        e.stopPropagation()
                        cancelEdit()
                      }}
                      disabled={editSaving}
                    >
                      Cancel
                    </button>
                    <button
                      className="small danger"
                      onClick={e => {
                        e.stopPropagation()
                        remove(l.id)
                      }}
                      disabled={editSaving}
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="small"
                      onClick={e => {
                        e.stopPropagation()
                        remove(l.id)
                      }}
                    >
                      Delete
                    </button>
                    {!settings.categories[l.type as EntryType]?.includes(l.category) && (
                      <button
                        className="small secondary"
                        title="Save as category"
                        onClick={e => {
                          e.stopPropagation()
                          favorite(l)
                        }}
                      >
                        Save action
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )
        })}
      </div>
      {bulkModalOpen && (
        <div className="photo-modal-backdrop" onClick={closeBulkUpdate}>
          <div className="photo-modal history-bulk-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="photo-modal-header">
              <span>Update entries ({selectedIds.length})</span>
              <button className="small secondary" onClick={closeBulkUpdate}>Close</button>
            </div>
            <div className="history-bulk-fields">
              <label>
                Date
                <input type="date" value={bulkDate} onChange={e => setBulkDate(e.target.value)} />
              </label>
              <label>
                Time
                <input type="time" value={bulkTime} onChange={e => setBulkTime(e.target.value)} />
              </label>
            </div>
            <div className="history-quick-actions">
              <button className="small secondary" onClick={() => setBulkToYesterday(9)}>Yesterday 9</button>
              <button className="small secondary" onClick={() => setBulkToYesterday(13)}>Yesterday 13</button>
              <button className="small secondary" onClick={() => setBulkToYesterday(19)}>Yesterday 19</button>
            </div>
            {bulkError && <div className="status error">{bulkError}</div>}
            <div className="history-bulk-actions">
              <button className="small secondary" onClick={closeBulkUpdate} disabled={bulkSaving}>Cancel</button>
              <button className="small button" onClick={applyBulkUpdate} disabled={bulkSaving}>
                {bulkSaving ? 'Updating…' : 'Apply update'}
              </button>
            </div>
          </div>
        </div>
      )}
      {photoModal && (
        <PhotoModal
          smallUrl={photoModal.smallUrl}
          largeUrl={photoModal.largeUrl}
          initialSize={photoModal.initialSize}
          onClose={closePhoto}
        />
      )}
    </div>
  )
}

function applyFilters(logs: LogEntry[], filters: Filters) {
  const { q, from, to } = filters
  const query = q.trim().toLowerCase()
  const fromDate = from ? new Date(from) : undefined
  const toDate = to ? new Date(to) : undefined
  if (fromDate) {
    fromDate.setHours(0, 0, 0, 0)
  }
  if (toDate) {
    toDate.setHours(23, 59, 59, 999)
  }

  return logs.filter(log => {
    if (query) {
      const haystack = `${log.category} ${log.notes ?? ''}`.toLowerCase()
      if (!haystack.includes(query)) {
        return false
      }
    }
    if (fromDate && new Date(log.date) < fromDate) {
      return false
    }
    if (toDate && new Date(log.date) > toDate) {
      return false
    }
    return true
  })
}

function formatDateInput(date: Date) {
  if (Number.isNaN(date.getTime())) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatTimeInput(date: Date) {
  if (Number.isNaN(date.getTime())) return ''
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

function formatDateTimeLocal(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return `${formatDateInput(date)}T${formatTimeInput(date)}`
}

function parseDateTimeLocal(value: string) {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return undefined
  return date.toISOString()
}

function parseDateTimeParts(date: string, time: string) {
  if (!date || !time) return undefined
  return parseDateTimeLocal(`${date}T${time}`)
}
